import Script from 'next/script'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { listModelAccessForUserRole, PermissionDetail } from '@common/util/permissions'
import { useAdminContext } from '~/components/hooks/useAdminContext'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { usePermissionContext } from '~/components/hooks/usePermissionContext'
import { Feature } from '~/graphql/generated/query.types'
import { isProd } from '~/util/env'
import { avatarSidToUrl } from '~/util/s3'
import { useFeatureFlag } from '~/util/useFeatureFlag'

import type { FrigadeGuideLaunchResult, FrigadeGuideToolInput } from '@common/model/frigadeGuide'

export type FrigadeAssistantToolCall = {
  toolCallId?: string
  toolName?: string
  name?: string
  input?: unknown
  args?: unknown
  arguments?: unknown
}

declare global {
  interface Window {
    FrigadeAssistant: {
      (config: {
        apiKey: string
        apiUrl: string
        assistantId: string
        userId: string
        userProperties: {
          firstName: string
          lastName: string
          imageUrl?: string
          email: string
          role: string
          permissions: PermissionDetail[]
        }
        showWidget?: boolean
        mode?: 'guide' | 'assistant'
      }): void
      handleToolCall?: (
        toolCall: FrigadeAssistantToolCall
      ) => Promise<FrigadeGuideLaunchResult | null>
      launchGuide?: (input: FrigadeGuideToolInput) => Promise<FrigadeGuideLaunchResult>
    }
  }
}

/**
 * Mounts the Frigade guide instance so Ask Rentana can forward its `frigade_guide` tool calls and
 * render step-by-step guides as on-page overlays. Frigade has no standalone UI here — the Help
 * button opens the Pylon help center, and all Frigade interaction flows through Ask Rentana.
 */
export function FrigadeAssistantWrapper({ children }: PropsWithChildren) {
  const user = useCurrentUser()
  const permissionContext = usePermissionContext()
  const { isImpersonating } = useAdminContext()
  const isFrigadeAssistantEnabled = useFeatureFlag(Feature.frigadeAssistant)
  const isRentanaChatEnabled = useFeatureFlag(Feature.rentanaChat)
  const frigadeApiKey = process.env.NEXT_PUBLIC_FRIGADE_API_KEY
  const frigadeAssistantId = process.env.NEXT_PUBLIC_FRIGADE_ASSISTANT_ID
  const isEnabled =
    isProd &&
    isFrigadeAssistantEnabled &&
    isRentanaChatEnabled &&
    !!frigadeApiKey &&
    !!frigadeAssistantId

  const [isFrigadeScriptLoaded, setIsFrigadeScriptLoaded] = useState(false)
  const isGuideInstanceMountedRef = useRef(false)

  const [firstName, ...lastNameParts] = user.fullName.split(' ')
  const lastName = lastNameParts.join(' ')
  const imageUrl = user.avatarSid ? avatarSidToUrl(user.avatarSid) : undefined
  const permissions = listModelAccessForUserRole(user.role, permissionContext)
  const guideConfig = {
    apiKey: frigadeApiKey ?? '',
    apiUrl: 'https://api.frigade.ai',
    assistantId: frigadeAssistantId ?? '',
    userId: user.sid,
    userProperties: {
      firstName: `${isImpersonating ? '(impersonated) ' : ''}${firstName ?? user.fullName}`,
      lastName: lastName ?? '',
      imageUrl,
      email: user.email,
      role: user.role,
      permissions,
    },
    showWidget: false,
    mode: 'guide' as const,
  }

  // Mount the guide instance once the SDK has loaded. Waiting on the script-loaded state (rather
  // than the script's onLoad) means it still mounts if the feature flags resolve to true later.
  useEffect(() => {
    if (!isEnabled || !isFrigadeScriptLoaded || isGuideInstanceMountedRef.current) {
      return
    }
    const mountFrigade = window.FrigadeAssistant
    if (!mountFrigade) {
      return
    }
    isGuideInstanceMountedRef.current = true
    mountFrigade(guideConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, isFrigadeScriptLoaded])

  if (!isEnabled) {
    return <>{children}</>
  }

  return (
    <>
      <Script
        id="frigade-assistant-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.FrigadeAssistant =
              window.FrigadeAssistant ||
              function (config) {
                (window.FrigadeAssistant.q = window.FrigadeAssistant.q || []).push(config);
              };
            window.FrigadeAssistant.on =
              window.FrigadeAssistant.on ||
              function (event, handler) {
                (window.FrigadeAssistant.on.q = window.FrigadeAssistant.on.q || []).push({
                  event,
                  handler,
                });
              };
          `,
        }}
      />
      <Script
        id="frigade-assistant-script"
        strategy="afterInteractive"
        src="https://app.frigade.com/cdn/frigade-assistant.min.js"
        onLoad={() => setIsFrigadeScriptLoaded(true)}
      />
      {children}
    </>
  )
}
